/**
 * AdminFormPage — full-page form shell for Admin Hub resources.
 *
 * Registered resources are resolved from the authenticated backend experience
 * API. A caller-supplied contract may contribute dynamic option values only;
 * it cannot add fields, actions, permissions, titles, or routes.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { Page, PageSection } from '../layout';
import { FormRenderer } from './FormRenderer';
import { PageErrorState, PageLoadingState, PageNotFoundState } from '../states';
import { resolveTKey } from '../../i18n/resolve-key';
import { useUiStore } from '../../state/ui';
import { useExperience } from '../../features/experience/ExperienceProvider';
import { useResolvedResource } from '../../features/experience/useResolvedExperience';
import type { ActionExecutionContext } from '../../runtime/actions/action-executor';
import type {
  FormDescriptor,
  FormFieldDescriptor,
  FormSubmissionContext,
  FormSubmissionResult,
  ActionDescriptor,
  TranslationKey,
} from '@finance/contracts';

export type AdminFormMode = 'create' | 'edit' | 'view';

export type AdminFormSuccessPolicy =
  | { kind: 'returnToList' }
  | { kind: 'navigateToDetail'; getCreatedId: (result: FormSubmissionResult) => string | undefined }
  | { kind: 'remainOnPage' }
  | { kind: 'explicitRoute'; path: string };

export interface AdminFormPageProps {
  resourceId?: string;
  mode?: AdminFormMode;
  titleKey?: TranslationKey;
  backPath?: string;
  contract?: FormDescriptor;
  onSubmit: (
    commandId: string,
    values: Record<string, unknown>,
    context: FormSubmissionContext,
  ) => Promise<FormSubmissionResult>;
  onAction?: (
    action: ActionDescriptor,
    context?: ActionExecutionContext,
  ) => void | Promise<void>;
  initialValues?: Record<string, unknown>;
  loadingInitialValues?: boolean;
  recordVersion?: number;
  successPolicy?: AdminFormSuccessPolicy;
  onConflictReload?: () => Promise<void>;
}

function mergeRuntimeOptions(
  authoritative: FormDescriptor,
  runtime: FormDescriptor | undefined,
): FormDescriptor {
  if (!runtime) return authoritative;
  const runtimeFields = new Map<string, FormFieldDescriptor>();
  for (const field of runtime.fields) {
    runtimeFields.set(String(field.id), field);
    runtimeFields.set(field.name, field);
  }
  return {
    ...authoritative,
    fields: authoritative.fields.map((field) => {
      const runtimeField = runtimeFields.get(String(field.id)) ?? runtimeFields.get(field.name);
      return runtimeField?.options ? { ...field, options: runtimeField.options } : field;
    }),
  };
}

function fillFirstRouteParameter(path: string, value: string): string {
  return path
    .replace(/\{[A-Za-z0-9_-]+\}/, encodeURIComponent(value))
    .replace(/:[A-Za-z0-9_-]+/, encodeURIComponent(value));
}

export function AdminFormPage({
  resourceId,
  mode,
  titleKey,
  backPath,
  contract,
  onSubmit,
  onAction,
  initialValues,
  loadingInitialValues,
  recordVersion,
  successPolicy = { kind: 'returnToList' },
  onConflictReload,
}: AdminFormPageProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { addToast } = useUiStore();
  const experience = useExperience();
  const resourceQuery = useResolvedResource(resourceId ?? '', { enabled: Boolean(resourceId) });
  const resolved = resourceQuery.data;
  const registeredContract = mode ? resolved?.forms[mode] : undefined;
  const effectiveContract = registeredContract
    ? mergeRuntimeOptions(registeredContract, contract)
    : contract;
  const effectiveTitleKey = titleKey
    ?? (mode === 'create'
      ? resolved?.resource.createTitleKey ?? resolved?.resource.listTitleKey
      : mode === 'edit'
        ? resolved?.resource.editTitleKey ?? resolved?.resource.listTitleKey
        : mode === 'view'
          ? resolved?.resource.detailTitleKey ?? resolved?.resource.listTitleKey
          : undefined);
  const effectiveBackPath = backPath ?? experience.getRoutePath(resolved?.resource.listRouteId);

  const handleSubmit = useCallback(
    async (
      commandId: string,
      values: Record<string, unknown>,
      context: FormSubmissionContext,
    ): Promise<FormSubmissionResult> => {
      const result = await onSubmit(commandId, values, context);
      if (result.status === 'succeeded' && effectiveContract && effectiveBackPath) {
        if (effectiveContract.submission.successMessageKey) {
          addToast({
            type: 'success',
            message: t(resolveTKey(effectiveContract.submission.successMessageKey)),
            duration: 5000,
          });
        }
        switch (successPolicy.kind) {
          case 'returnToList':
            navigate(effectiveBackPath);
            break;
          case 'navigateToDetail': {
            const createdId = successPolicy.getCreatedId(result);
            const detailPath = experience.getRoutePath(resolved?.resource.detailRouteId);
            navigate(createdId && detailPath ? fillFirstRouteParameter(detailPath, createdId) : effectiveBackPath);
            break;
          }
          case 'explicitRoute':
            navigate(successPolicy.path);
            break;
          case 'remainOnPage':
          default:
            break;
        }
      }
      return result;
    },
    [addToast, effectiveBackPath, effectiveContract, experience, navigate, onSubmit, resolved, successPolicy, t],
  );

  const handleAction = useCallback(
    (action: ActionDescriptor, context?: ActionExecutionContext) => {
      if (action.id === 'action.cancel' && effectiveBackPath) {
        navigate(effectiveBackPath);
      } else {
        return onAction?.(action, context);
      }
    },
    [effectiveBackPath, navigate, onAction],
  );

  if (resourceId && (resourceQuery.isLoading || experience.loading)) return <PageLoadingState />;
  if (resourceId && (resourceQuery.isError || experience.error)) {
    return <PageErrorState onRetry={() => void Promise.all([resourceQuery.refetch(), experience.refresh()])} />;
  }
  if (resourceId && !resolved) return <PageNotFoundState />;
  if (!effectiveContract || !effectiveTitleKey || !effectiveBackPath) {
    throw new Error(
      'AdminFormPage requires a registered resource and mode, or explicit contract, titleKey, and backPath.',
    );
  }

  const descriptionKey = effectiveContract.descriptionKey ?? resolved?.resource.descriptionKey;
  const canEdit =
    mode === 'view' &&
    resolved?.forms.edit !== undefined &&
    resolved?.resource.editRouteId !== undefined;
  const editRoute = canEdit
    ? experience.getRoutePath(resolved.resource.editRouteId)
    : undefined;
  const recordIdForEdit =
    typeof initialValues?.['id'] === 'string'
      ? initialValues['id']
      : undefined;
  const editPath = editRoute && recordIdForEdit ? fillFirstRouteParameter(editRoute, recordIdForEdit) : undefined;

  const hasCancelAction = Boolean(effectiveContract.actions?.some(a => a.id === 'action.cancel'));

  return (
    <Box sx={{ width: '100%', maxWidth: theme.financeLayout.form.maxWidth, mx: 'auto', minWidth: 0 }}>
      <Page
        title={t(resolveTKey(effectiveTitleKey))}
        subtitle={descriptionKey ? t(resolveTKey(descriptionKey)) : undefined}
        actionNode={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {editPath && (
              <Button
                variant="contained"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => navigate(editPath)}
              >
                {t('action.edit')}
              </Button>
            )}
            {!hasCancelAction && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(effectiveBackPath)}
              >
                {t('common:back')}
              </Button>
            )}
          </Box>
        }
      >
        <PageSection>
          <FormRenderer
            contract={effectiveContract}
            onSubmit={handleSubmit}
            onAction={handleAction}
            initialValues={initialValues}
            loadingInitialValues={loadingInitialValues}
            recordVersion={recordVersion}
            showHeader={false}
            onConflictReload={onConflictReload}
          />
        </PageSection>
      </Page>
    </Box>
  );
}
